import { useState, useEffect } from "react";
import Bandera from "./components/Banderas";
import "../css/DragDrop.css";

import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

interface Seleccion {
  id: number;
  nombre: string;
  bandera: string;
}

interface OrdenGrupo {
  primero: number;
  segundo: number;
  tercero: number;
  cuarto: number;
}

interface DragDropProps {
  grupo: string;
  equipos: Seleccion[];
  bloqueado: boolean;
  onGuardarOrden?: (orden: OrdenGrupo) => void;
  estado: "inicial" | "esperando" | "parcial" | "perfecto" | "fallo";
}

/* =========================
   Item draggable
========================= */
const SortableItem = ({
  eq,
  index,
  grupo,
  bloqueado,
  esMobile,
  moverArriba,
  moverAbajo
}: {
  eq: Seleccion;
  index: number;
  grupo: string;
  bloqueado: boolean;
  esMobile: boolean;
  moverArriba: (index: number) => void;
  moverAbajo: (index: number) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: eq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const esDirecto = index < 2;
  const esTercero = index === 2;
  const esEliminado = index === 3;



  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(!bloqueado && !esMobile ? { ...attributes, ...listeners } : {})}
      className={`dragdrop-item
        ${esDirecto ? "clasificado" : ""}
        ${esTercero ? "tercero" : ""}
        ${esEliminado ? "eliminado" : ""}
        ${bloqueado ? "item-bloqueado" : ""}
      `}
    >
      <span className="drag-handle">⋮⋮</span>

      <Bandera
        grupo={grupo}
        nombre={eq.bandera.replace(/\.(png|webp)$/, "")}
        alt={eq.nombre}
        size={40}
      />
      <div className="dragdrop-info">
        <strong>{index + 1}°</strong>
        <span className="nombre-equipo">{eq.nombre}</span>
      </div>

      

      <span className="dragdrop-status">
        {esDirecto && "✔"}
        {esTercero && "⭐"}
        {esEliminado && "✘"}
      </span>

      {esMobile && !bloqueado && (
        <div className="mobile-controls">
          <button onClick={() => moverArriba(index)}>↑</button>
          <button onClick={() => moverAbajo(index)}>↓</button>
        </div>
      )}
    </div>
    
  );
};






/* =========================
   Componente principal
========================= */
const DragDrop = ({
  grupo,
  equipos,
  bloqueado,
  onGuardarOrden,
  estado
}: DragDropProps) => {

  const [orden, setOrden] = useState<Seleccion[]>(equipos);

  useEffect(() => {
  setOrden(equipos);
}, [grupo]); 


 const [esMobile, setEsMobile] = useState(window.innerWidth < 768);

useEffect(() => {
  const handleResize = () => {
    setEsMobile(window.innerWidth < 768);
  };

  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

const sensors = useSensors(
  useSensor(MouseSensor),
  useSensor(TouchSensor, {
    activationConstraint: {
      delay: 150,
      tolerance: 5,
    },
  })
);

const moverArriba = (index: number) => {
  if (index === 0) return;

  const nueva = [...orden];
  [nueva[index - 1], nueva[index]] = [nueva[index], nueva[index - 1]];

  setOrden(nueva);
  onGuardarOrden?.({
  primero: nueva[0]?.id,
  segundo: nueva[1]?.id,
  tercero: nueva[2]?.id,
  cuarto: nueva[3]?.id
});
};

const moverAbajo = (index: number) => {
  if (index === orden.length - 1) return;

  const nueva = [...orden];
  [nueva[index + 1], nueva[index]] = [nueva[index], nueva[index + 1]];

  setOrden(nueva);
  onGuardarOrden?.({
  primero: nueva[0]?.id,
  segundo: nueva[1]?.id,
  tercero: nueva[2]?.id,
  cuarto: nueva[3]?.id
});
};
  /* =========================
     Drag end
  ========================= */
  const handleDragEnd = (event: any) => {
    if (bloqueado) return;

    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = orden.findIndex(e => e.id === active.id);
    const newIndex = orden.findIndex(e => e.id === over.id);

    const nuevaLista = [...orden];
    const [movido] = nuevaLista.splice(oldIndex, 1);
    nuevaLista.splice(newIndex, 0, movido);

    setOrden(nuevaLista);
    onGuardarOrden?.({
  primero: nuevaLista[0].id,
  segundo: nuevaLista[1].id,
  tercero: nuevaLista[2].id,
  cuarto: nuevaLista[3].id
});
  };

  return (
    <div className={`grupo-card estado-${estado}`}>

      {/* VIDEOS */}
      {estado === "esperando" && (
        <video
          className="grupo-video"
          src="/estados2/esperando.webm?v=2"
          autoPlay
          muted
          loop
          playsInline
        />
      )}

      {estado === "perfecto" && (
        <video
          className="grupo-video"
          src="/estados2/perfecto.webm"
          autoPlay
          muted
          loop
          playsInline
        />
      )}

      {estado !== "esperando" && estado !== "perfecto" && (
        <div className="grupo-bg" />
      )}

      <div className="grupo-overlay" />

      <div className="grupo-content">

        {/* HEADER */}
        <div className="grupo-header">
          <h3>Grupo {grupo}</h3>
          <span className="grupo-badge">
            {estado === "inicial" && "Ordená tu predicción"}
            {estado === "esperando" && "🕒 Confirmado"}
            {estado === "parcial" && "✔ +50 pts"}
            {estado === "perfecto" && "🏆 +200 pts"}
            {estado === "fallo" && "❌ 0 pts"}
          </span>
        </div>

        <div className="dragdrop-container">

          {!bloqueado && (
            <p className="dragdrop-explicacion">
              Acierto parcial : acertas una posición<strong>+50 pts</strong>. Orden perfecto: <strong>+200 pts</strong> 
            </p>
          )}

          {bloqueado && estado === "esperando" && (
              <div className="dragdrop-overlay">
                ✅ Esperando Resultados...
              </div>
            )}

          {/* =========================
             DND CONTEXT
          ========================= */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orden.map(e => e.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={`dragdrop-list ${bloqueado ? "bloqueado" : ""}`}>
                {orden.map((eq, index) => (
                  <SortableItem
                  key={eq.id}
                  eq={eq}
                  index={index}
                  grupo={grupo}
                  bloqueado={bloqueado}
                  esMobile={esMobile}
                  moverArriba={moverArriba}
                  moverAbajo={moverAbajo}
                />
                ))}
              </div>
            </SortableContext>
          </DndContext>

        </div>
      </div>
    </div>
  );
};

export default DragDrop;